import { useState } from 'react'
import {
  Box,
  Divider,
  Flex,
  IconButton,
  Text,
  Tooltip,
  useToast,
  Spinner,
  Textarea
} from '@chakra-ui/react'
import { MdEdit, MdDelete, MdSave, MdClose } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { expireDateVerify } from '../../utils/fns/expireDate'
import { ModalContentComponent } from '../Modal/modalContent'
import { NotesData } from '../../hooks/notes/useNotes'
import { apiAuth } from '../../services/apiAuth'
import { queryClient } from '../../services/reactQuery'
import { Input } from '../Form/Input'

const BASE_URL = 'clinics/notes/'

interface NotesModalProps {
  isOpen: boolean
  onClose: () => void
  patientName: string
  patientId: number
  notes: NotesData[]
  setNotes: (notes: NotesData[]) => void
}

export function NotesModal({
  isOpen,
  onClose,
  patientName,
  notes,
  setNotes,
  patientId
}: NotesModalProps) {
  const toast = useToast()
  const [t] = useTranslation('pageScheduling')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingMessage, setEditingMessage] = useState('')
  const [editingEventDate, setEditingEventDate] = useState('')
  const [loadingId, setLoadingId] = useState<number | null>(null)

  console.log(editingEventDate)

  const startEditing = (note: NotesData) => {
    setEditingId(note.id)
    setEditingMessage(note.message)
    setEditingEventDate(note.event_date.split('T')[0])
  }

  const handleSave = async (note: NotesData) => {
    setLoadingId(note.id)
    console.log(note)
    try {
      const payload = {
        name: note.name,
        type: note.type,
        event_date: `${editingEventDate}T00:00`,
        message: editingMessage,
        patients_ids: [patientId]
      }

      const { data } = await apiAuth.patch(`${BASE_URL}${note.id}/`, payload)

      setNotes(
        notes.map((n) =>
          n.id === note.id
            ? {
                ...n,
                message: editingMessage,
                event_date: editingEventDate,
                event_date_format: data?.event_date_format
              }
            : n
        )
      )

      toast({ title: t('notes.updated'), status: 'success', duration: 3000 })
      setEditingId(null)
    } catch (err: unknown) {
      toast({
        title: t('errors.update_failed'),
        status: 'error',
        duration: 3000
      })
    } finally {
      setLoadingId(null)
      queryClient.invalidateQueries('scheduling')
    }
  }

  const handleDelete = async (noteId: number) => {
    setLoadingId(noteId)
    try {
      await apiAuth.delete(`${BASE_URL}${noteId}/`)
      setNotes(notes.filter((n) => n.id !== noteId))
      toast({ title: t('notes.deleted'), status: 'success', duration: 3000 })
      if (editingId === noteId) setEditingId(null)
    } catch (err) {
      toast({
        title: t('errors.delete_failed'),
        status: 'error',
        duration: 3000
      })
    } finally {
      setLoadingId(null)
      queryClient.invalidateQueries('scheduling')
    }
  }

  return (
    <ModalContentComponent
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('notes.title')} ${patientName}`}
      notRenderFooter
    >
      <Flex flex="1" flexDirection="column" overflowY="auto">
        {notes?.length === 0 && (
          <Text textAlign="center" color="gray.500" py="6">
            {t('notes.empty')}
          </Text>
        )}

        {notes?.map((note) => (
          <Flex key={note.id} w="100%" flexDir="column" rounded="md">
            <Box p="4" position="relative">
              <Flex justify="space-between" align="start">
                <Text
                  fontSize="14px"
                  fontWeight="semibold"
                  color={expireDateVerify(note.event_date)}
                >
                  {`${note.event_date_format} - ${t('notes.created_by')} ${
                    note.created_by_name
                  }`}
                </Text>

                <Flex gap="2">
                  {editingId === note.id ? (
                    <>
                      <Tooltip label={t('actions.save')}>
                        <IconButton
                          size="sm"
                          aria-label="save"
                          icon={<MdSave />}
                          colorScheme="green"
                          isLoading={loadingId === note.id}
                          onClick={() => handleSave(note)}
                        />
                      </Tooltip>
                      <Tooltip label={t('actions.cancel')}>
                        <IconButton
                          size="sm"
                          aria-label="cancel"
                          icon={<MdClose />}
                          onClick={() => setEditingId(null)}
                        />
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip label={t('actions.edit')}>
                        <IconButton
                          size="sm"
                          aria-label="edit"
                          icon={<MdEdit />}
                          onClick={() => startEditing(note)}
                        />
                      </Tooltip>
                      <Tooltip label={t('actions.delete')}>
                        <IconButton
                          size="sm"
                          aria-label="delete"
                          icon={
                            loadingId === note.id ? (
                              <Spinner size="xs" />
                            ) : (
                              <MdDelete />
                            )
                          }
                          colorScheme="red"
                          onClick={() => handleDelete(note.id)}
                        />
                      </Tooltip>
                    </>
                  )}
                </Flex>
              </Flex>

              {editingId === note.id ? (
                <Flex gap={2} flexDir="column">
                  <Input
                    name="event"
                    type="date"
                    mt="4"
                    value={editingEventDate}
                    onChange={(e) => setEditingEventDate(e.target.value)}
                  />
                  <Textarea
                    mt="2"
                    value={editingMessage}
                    onChange={(e) => setEditingMessage(e.target.value)}
                    placeholder={t('notes.message')}
                  />
                </Flex>
              ) : (
                <Text mt="2">{note.message}</Text>
              )}
            </Box>
            <Divider />
          </Flex>
        ))}
      </Flex>
    </ModalContentComponent>
  )
}
